import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../theme";
import PersonIcon from "@mui/icons-material/Person";
import apiService from "../../../../../service/Api";

interface CrewMember {
  rank: number;
  crew_member_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  streams_attended: number;
}

interface CrewAttendanceLeaderboardResponse {
  month: string;
  total_participants: number;
  leaderboard: CrewMember[];
}

interface LeaderboardCardProps {
  members: CrewMember[];
}

const StreamsLeaderboard = () => {
  const [members, setMembers] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);

        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

        const res = await apiService.get<CrewAttendanceLeaderboardResponse>(
          `/api/management/dashboard/crew-attendance-leaderboard/?month=${month}`,
        );
        setMembers(res.data?.leaderboard ?? []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch attendance leaderboard.";
        setError(errorMessage);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return <LeaderboardCard members={members} />;
};

const LeaderboardCard = ({ members }: LeaderboardCardProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: colors.primary[900],
        mt: 2,
      }}
    >
      <Box sx={{ bgcolor: "background.paper", px: 2, py: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.05em",
            color: "#424242",
            textTransform: "uppercase",
          }}
        >
          Stream Attendance Leaderboard
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {/* Header Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "60px 50px 1fr 100px",
            alignItems: "center",
            px: 1.5,
            py: 1,
            borderBottom: "2px solid #bdbdbd",
            mb: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#616161",
              textTransform: "uppercase",
            }}
          >
            Rank
          </Typography>
          <Typography variant="caption" />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#616161",
              textTransform: "uppercase",
            }}
          >
            Crew
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#616161",
              textTransform: "uppercase",
              textAlign: "right",
            }}
          >
            Total Streams
          </Typography>
        </Box>

        {/* Data Rows */}
        {members.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#9e9e9e", textAlign: "center", py: 2 }}
          >
            No leaderboard data available.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {members.map((member) => (
              <LeaderboardRow
                key={member.crew_member_id}
                member={member}
                rank={member.rank}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const LeaderboardRow = ({
  member,
  rank,
}: {
  member: CrewMember;
  rank: number;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "60px 50px 1fr 100px",
        alignItems: "center",
        borderRadius: 1,
        bgcolor: colors.primary[900],
        px: 1.5,
        py: 1,
      }}
    >
      {/* Rank */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 700, color: "#757575", fontSize: 16 }}>
          {rank}
        </Typography>
      </Box>

      {/* Avatar */}
      <Avatar sx={{ width: 36, height: 36, bgcolor: "#9e9e9e" }}>
        <PersonIcon sx={{ color: "#616161", fontSize: 20 }} />
      </Avatar>

      {/* Crew Name */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "#424242",
          textTransform: "uppercase",
        }}
      >
        {member.first_name} {member.last_name}
      </Typography>

      {/* Total Streams */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: "#2e7d32", textAlign: "right" }}
      >
        {member.streams_attended}
      </Typography>
    </Box>
  );
};

export default StreamsLeaderboard;
